import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { DistributionEntry, Flag, FlagDefinition, Rule, Segment } from "@spoonity/flags-contract";
import { getDefinition, getFlag, killFlag, listSegments, putDefinition, restoreFlag } from "../../api/endpoints";
import { ApiError, isVersionConflict } from "../../api/errors";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EmptyState } from "../../components/EmptyState";
import { useToast } from "../../components/Toast";
import { canWrite, useAuth } from "../../auth/AuthContext";
import { useWorkspaceEnv } from "../../state/WorkspaceEnvContext";
import { DistributionEditor, distributionErrors } from "./DistributionEditor";
import { HistoryPanel } from "./HistoryPanel";
import { RulesBuilder } from "./RulesBuilder";
import { TreatmentsEditor, treatmentErrors, type EditableTreatment } from "./TreatmentsEditor";

interface FormState {
  enabled: boolean;
  treatments: EditableTreatment[];
  defaultTreatment: string;
  trafficAllocation: number;
  rules: Rule[];
  defaultRule: DistributionEntry[];
  comment: string;
}

function toEditable(def: FlagDefinition): FormState {
  return {
    enabled: def.enabled,
    treatments: def.treatments.map((t) => ({ name: t.name, config: t.config ?? "" })),
    defaultTreatment: def.defaultTreatment,
    trafficAllocation: def.trafficAllocation,
    rules: def.rules,
    defaultRule: def.defaultRule,
    comment: "",
  };
}

function blankForm(): FormState {
  return {
    enabled: false,
    treatments: [
      { name: "on", config: "" },
      { name: "off", config: "" },
    ],
    defaultTreatment: "off",
    trafficAllocation: 100,
    rules: [],
    defaultRule: [
      { treatment: "on", percent: 0 },
      { treatment: "off", percent: 100 },
    ],
    comment: "",
  };
}

export function FlagDetailPage() {
  const { flagKey = "" } = useParams();
  const { identity } = useAuth();
  const { workspace, environment, environments, selectEnvironment } = useWorkspaceEnv();
  const { pushError, push } = useToast();
  const writable = canWrite(identity?.role);

  const [flag, setFlag] = useState<Flag | null>(null);
  const [version, setVersion] = useState(0);
  const [killed, setKilled] = useState(false);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [confirmKill, setConfirmKill] = useState<"kill" | "restore" | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!workspace) return;
    let cancelled = false;
    (async () => {
      try {
        const f = await getFlag(workspace.id, flagKey);
        if (!cancelled) setFlag(f);
      } catch (err) {
        if (!cancelled) pushError(err, "Could not load flag");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspace, flagKey, pushError]);

  async function loadDefinition() {
    if (!environment) return;
    setLoading(true);
    setConflict(false);
    try {
      const detail = await getDefinition(environment.id, flagKey);
      setForm(toEditable(detail.definition));
      setVersion(detail.definition.version);
      setKilled(detail.definition.killed);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setForm(blankForm());
        setVersion(0);
        setKilled(false);
      } else {
        pushError(err, "Could not load definition");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadDefinition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment?.id, flagKey]);

  useEffect(() => {
    if (!environment) return;
    let cancelled = false;
    (async () => {
      try {
        const page = await listSegments(environment.id, { limit: 200 });
        if (!cancelled) setSegments(page.items);
      } catch {
        // segment picker just stays empty; not fatal
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [environment?.id]);

  const treatmentNames = useMemo(() => form?.treatments.map((t) => t.name) ?? [], [form]);

  const errors = useMemo(() => {
    if (!form) return [];
    const list: string[] = [];
    list.push(...treatmentErrors(form.treatments));
    if (!treatmentNames.includes(form.defaultTreatment)) {
      list.push(`defaultTreatment "${form.defaultTreatment}" is not a declared treatment`);
    }
    list.push(...distributionErrors(form.defaultRule, treatmentNames).map((e) => `default rule: ${e}`));
    form.rules.forEach((r, i) => {
      list.push(...distributionErrors(r.distribution, treatmentNames).map((e) => `rule ${i + 1} (${r.id}): ${e}`));
      if (r.conditions.length === 0) list.push(`rule ${i + 1} (${r.id}): at least one condition is required`);
      r.conditions.forEach((c) => {
        // Mirrors the contract's own check (packages/contract/src/definition.ts): only
        // "between" enforces min <= max, so the client doesn't invent a stricter rule.
        if (c.matcher === "between" && c.min > c.max) {
          list.push(`rule ${i + 1} (${r.id}): min must be <= max`);
        }
      });
    });
    const ruleIds = new Set<string>();
    for (const r of form.rules) {
      if (ruleIds.has(r.id)) list.push(`duplicate rule id "${r.id}"`);
      ruleIds.add(r.id);
    }
    if (form.trafficAllocation < 0 || form.trafficAllocation > 100) {
      list.push("traffic allocation must be between 0 and 100");
    }
    return list;
  }, [form, treatmentNames]);

  if (!workspace) return null;
  if (loading || !form) {
    return <p className="muted">Loading…</p>;
  }

  async function save() {
    if (!environment || !form) return;
    setSaving(true);
    try {
      const detail = await putDefinition(environment.id, flagKey, {
        expectedVersion: version,
        enabled: form.enabled,
        treatments: form.treatments.map((t) => ({ name: t.name, config: t.config.trim() ? t.config : undefined })),
        defaultTreatment: form.defaultTreatment,
        trafficAllocation: form.trafficAllocation,
        rules: form.rules,
        defaultRule: form.defaultRule,
        comment: form.comment || undefined,
      });
      setVersion(detail.definition.version);
      setKilled(detail.definition.killed);
      setForm(toEditable(detail.definition));
      push({ kind: "success", title: "Definition saved" });
    } catch (err) {
      if (isVersionConflict(err)) {
        setConflict(true);
      } else {
        pushError(err, "Could not save definition");
      }
    } finally {
      setSaving(false);
    }
  }

  async function toggleKill() {
    if (!environment) return;
    try {
      const detail = confirmKill === "kill" ? await killFlag(environment.id, flagKey) : await restoreFlag(environment.id, flagKey);
      setKilled(detail.definition.killed);
      setVersion(detail.definition.version);
      push({ kind: "success", title: confirmKill === "kill" ? "Flag killed" : "Flag restored" });
    } catch (err) {
      pushError(err, "Could not update kill state");
    } finally {
      setConfirmKill(null);
    }
  }

  return (
    <div>
      <div className="row between wrap">
        <div>
          <h1>{flag?.name ?? flagKey}</h1>
          <p className="muted">{flag?.description}</p>
          {flag?.tags.map((t) => (
            <span key={t} className="tag">
              {t}
            </span>
          ))}
        </div>
        <div className="row">
          <Link className="btn" to={`/evaluate?flag=${flagKey}`}>
            Evaluate
          </Link>
          <Link className="btn" to={`/impressions?flag=${flagKey}`}>
            Impressions
          </Link>
        </div>
      </div>

      <div className="tabs" role="tablist" aria-label="Environment">
        {environments.map((env) => (
          <button
            key={env.id}
            role="tab"
            aria-selected={env.id === environment?.id}
            className={env.id === environment?.id ? "active" : ""}
            onClick={() => selectEnvironment(env.id)}
          >
            {env.name}
          </button>
        ))}
      </div>

      {conflict && (
        <div className="card" style={{ borderColor: "var(--color-danger)" }}>
          <strong>Someone else changed this definition.</strong>
          <p>Reload to see the latest version, then re-apply your changes.</p>
          <button className="btn primary" onClick={() => void loadDefinition()}>
            Reload
          </button>
        </div>
      )}

      <div className="card">
        <div className="row wrap">
          <label className="row" style={{ gap: "var(--space-1)" }}>
            <input
              type="checkbox"
              checked={form.enabled}
              disabled={!writable}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            Enabled
          </label>
          <span className={`pill ${killed ? "killed" : "live"}`}>{killed ? "Killed" : "Live"}</span>
          <div className="spacer" />
          {writable &&
            (killed ? (
              <button className="btn" onClick={() => setConfirmKill("restore")}>
                Restore
              </button>
            ) : (
              <button className="btn danger" onClick={() => setConfirmKill("kill")}>
                Kill switch
              </button>
            ))}
        </div>

        <h3>Treatments</h3>
        <TreatmentsEditor
          treatments={form.treatments}
          readOnly={!writable}
          onChange={(treatments) => setForm({ ...form, treatments })}
        />

        <div className="field" style={{ marginTop: "var(--space-4)" }}>
          <label htmlFor="flag-default-treatment">Default treatment</label>
          <select
            id="flag-default-treatment"
            className="select"
            value={form.defaultTreatment}
            disabled={!writable}
            onChange={(e) => setForm({ ...form, defaultTreatment: e.target.value })}
          >
            {treatmentNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="flag-traffic-allocation">Traffic allocation (%)</label>
          <input
            id="flag-traffic-allocation"
            className="select"
            type="number"
            min={0}
            max={100}
            style={{ maxWidth: 120 }}
            value={form.trafficAllocation}
            disabled={!writable}
            onChange={(e) => setForm({ ...form, trafficAllocation: Number(e.target.value) })}
          />
          <span className="hint">Keys outside allocation get the default treatment.</span>
        </div>

        <h3>Rules</h3>
        <RulesBuilder
          rules={form.rules}
          treatmentNames={treatmentNames}
          segmentNames={segments.map((s) => s.name)}
          readOnly={!writable}
          onChange={(rules) => setForm({ ...form, rules })}
        />

        <h3>Default rule</h3>
        <div data-testid="default-rule">
          <DistributionEditor
            distribution={form.defaultRule}
            treatmentNames={treatmentNames}
            readOnly={!writable}
            onChange={(defaultRule) => setForm({ ...form, defaultRule })}
          />
        </div>

        {writable && (
          <>
            <div className="field" style={{ marginTop: "var(--space-4)" }}>
              <label htmlFor="flag-comment">Comment (optional, recorded in the audit log)</label>
              <input
                id="flag-comment"
                className="select"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
              />
            </div>
            {errors.length > 0 && (
              <div className="card" style={{ background: "var(--color-danger-bg)", borderColor: "transparent" }}>
                <strong>Fix before saving:</strong>
                <ul>
                  {errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
            <button className="btn primary" onClick={save} disabled={saving || errors.length > 0}>
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        )}
      </div>

      <div className="card">
        <div className="row between">
          <h3>Version history</h3>
          <button className="link-button" onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? "Hide" : "Show"}
          </button>
        </div>
        {showHistory && environment && <HistoryPanel environmentId={environment.id} flagKey={flagKey} />}
      </div>

      {confirmKill && (
        <ConfirmDialog
          title={confirmKill === "kill" ? "Kill this flag?" : "Restore this flag?"}
          message={
            confirmKill === "kill"
              ? "Every key will immediately get the default treatment until restored."
              : "Rules and traffic allocation will apply again."
          }
          confirmLabel={confirmKill === "kill" ? "Kill" : "Restore"}
          danger={confirmKill === "kill"}
          onConfirm={toggleKill}
          onClose={() => setConfirmKill(null)}
        />
      )}
    </div>
  );
}

export function FlagDetailFallback() {
  return <EmptyState title="Flag not found" />;
}
